import { useEffect, useState } from 'react';
import type { QuestionResult } from '@floorpoll/shared';
import { CodeCells } from './CodeCells.js';
import { ResultBars } from './ResultBars.js';

const DEMO_CODE = 'GK7T2M';
const DEMO_QUESTION = 'Where should we do the offsite?';
const DEMO_OPTIONS = ['Lisbon', 'Reykjavík', 'Mexico City'];

/**
 * Vote counts, step by step — the room making up its mind over ~8 seconds.
 *
 * Small deltas and a short step are load-bearing, not taste. ResultBars eases
 * `count` through useCountUp but prints `percentage` straight from props, so
 * for the length of an ease the two disagree; live, votes arrive one at a time
 * and the gap is invisible. Feed it +7 in one go and the panel reads "18 · 51%"
 * when 18 of 38 is 47%. At +2 the discrepancy stays under a point, and the step
 * outlasts the 550ms ease so each count settles before the next arrives.
 */
const BURSTS = [
  [26, 14, 10],
  [28, 15, 10],
  [30, 16, 11],
  [32, 17, 12],
  [34, 18, 12],
  [36, 19, 13],
  [38, 20, 14],
  [40, 21, 15],
  [42, 22, 15],
  [43, 23, 16],
  [45, 24, 17],
  [46, 25, 17],
];

const BURST_MS = 700;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function tally(counts: number[]): QuestionResult[] {
  const totalVotes = counts.reduce((sum, count) => sum + count, 0);

  return [
    {
      questionId: 'showcase',
      text: DEMO_QUESTION,
      totalVotes,
      options: DEMO_OPTIONS.map((text, index) => {
        const count = counts[index] ?? 0;
        return {
          optionId: `showcase-${index}`,
          text,
          count,
          percentage: Math.round((count / totalVotes) * 1000) / 10,
        };
      }),
    },
  ];
}

/**
 * The product's own big-screen pieces, fed fixture counts.
 *
 * One prop: `size` goes straight to `CodeCells`. The auth panel keeps `md`;
 * the landing hero uses `lg`. Nothing else about the demo varies between the
 * two — same code, same question, same burst schedule.
 *
 * aria-hidden: the counts are fixtures. Announcing them as live results would
 * be a lie told on a timer. The cue list (auth) or "How it works" (landing)
 * carries the same claims in words and stays exposed.
 */
export function LiveDemo({ size }: { size: 'md' | 'lg' }) {
  // Read once at mount. Reduced motion opts out of the demo entirely: the
  // panel opens on the settled tally instead of trickling votes in.
  const [still] = useState(prefersReducedMotion);
  const [burst, setBurst] = useState(still ? BURSTS.length - 1 : 0);

  useEffect(() => {
    // Runs once through and stops — a poll that closes, not a loop. Looping
    // would have to drop the counts back down, which reads as votes being
    // taken away.
    if (still || burst >= BURSTS.length - 1) return;
    const timer = setTimeout(() => setBurst((current) => current + 1), BURST_MS);
    return () => clearTimeout(timer);
  }, [still, burst]);

  return (
    <div className="stage-demo" aria-hidden="true">
      <div className="stage-demo-head">
        <span className="lamp">Live</span>
        <p className="join-url">floorpoll.app/j/{DEMO_CODE}</p>
      </div>
      <CodeCells code={DEMO_CODE} size={size} flip={!still} />
      <ResultBars questions={tally(BURSTS[burst] ?? [])} animated />
    </div>
  );
}
